import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatusBadge,
  Tabs,
  Textarea,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import { useToast } from "@/components/ui/Toast";
import { PageHeader } from "@/components/layout/PageHeader";
import { ApiError, extractErrorMessage } from "@/api/client";
import { useDateFormatter } from "@/utils/datetime";
import {
  prequalDocumentsApi,
  type PrequalDocument,
  type PrequalDocumentBlock,
  type PrequalDocumentBlockError,
  type PrequalDocumentSet,
  type PrequalDocumentVersion,
} from "@/api/prequalificationDocuments";
import {
  useDocumentSets,
  usePrequalDocuments,
  usePublishPrequalDocument,
  useResetPrequalBlock,
  useRestorePrequalDocument,
  useSavePrequalDraft,
} from "@/hooks/prequalification/usePrequalDocuments";
import { blockNoteFor } from "./prequalDocumentNotes";

const blockId = (b: { blockKey: string; variant: string }) => `${b.blockKey}|${b.variant}`;
/** The server's current working text for a block: an unpublished draft, else
 *  what renders live (published-else-default). */
const baselineOf = (b: PrequalDocumentBlock) => b.draftText ?? b.effectiveText;

/** Resolve a violation's block label off the live document (fallback: its key). */
const labelForViolation = (doc: PrequalDocument, v: { blockKey: string; variant: string }) =>
  doc.blocks.find((b) => b.blockKey === v.blockKey && b.variant === v.variant)?.label ?? v.blockKey;

function readBlockErrors(err: unknown): PrequalDocumentBlockError[] {
  if (err instanceof ApiError && Array.isArray(err.details)) {
    return (err.details as PrequalDocumentBlockError[])
      .filter((d) => d && typeof d.message === "string")
      .map((d) => ({ ...d, severity: d.severity === "Warning" ? "Warning" : "Error" }));
  }
  return [];
}

export function PrequalDocumentEditorPage() {
  const toast = useToast();
  const { date: formatDate } = useDateFormatter();

  // The selected document set: null = Phoenix Default (today's copy), else a
  // partner brand id. Every read/mutation/preview below is scoped to it.
  const [partnerBrandId, setPartnerBrandId] = useState<string | null>(null);
  const setsQuery = useDocumentSets();

  const query = usePrequalDocuments(partnerBrandId);
  const docs = useMemo(() => query.data ?? [], [query.data]);

  // Which document tab is open. Seeded from `?doc=` so a caller can deep-link a
  // specific tab (the Prequalification page's fine-print header action links to
  // `?doc=funnel-fine-print`).
  //
  // Read at PARSE TIME, in the useState initializer — deliberately NOT in a
  // post-mount effect. An effect that set this after mount would re-key the reads
  // below and fetch the page twice, which is the double-fetch this codebase has
  // hit before. An absent, blank or unrecognized value needs no special case: it
  // simply finds no document and falls through to `docs[0]`, exactly as before.
  const [searchParams] = useSearchParams();
  const [activeKey, setActiveKey] = useState<string | null>(() => searchParams.get("doc"));
  const active = useMemo(
    () => docs.find((d) => d.documentKey === activeKey) ?? docs[0],
    [docs, activeKey],
  );

  // In-progress edits, keyed docKey|blockKey|variant. Absent = show the server baseline.
  const [edits, setEdits] = useState<Record<string, string>>({});
  // Per-block validation items (Warnings + Errors), keyed docKey|blockKey|variant.
  const [blockErrors, setBlockErrors] = useState<Record<string, PrequalDocumentBlockError[]>>({});

  const save = useSavePrequalDraft();
  const publish = usePublishPrequalDocument();
  const restore = useRestorePrequalDocument();
  const reset = useResetPrequalBlock();

  const [publishOpen, setPublishOpen] = useState(false);
  // Publish dialog step: the plain confirm, an override-warnings gate, or a blocking-errors view.
  const [publishStep, setPublishStep] = useState<"confirm" | "override" | "errors">("confirm");
  const [publishViolations, setPublishViolations] = useState<PrequalDocumentBlockError[]>([]);
  const [overrideAck, setOverrideAck] = useState(false);
  const [restoreVersion, setRestoreVersion] = useState<PrequalDocumentVersion | null>(null);
  const [preview, setPreview] = useState<{ url: string; loading: boolean } | null>(null);

  const editKey = (doc: PrequalDocument, b: PrequalDocumentBlock) => `${doc.documentKey}|${blockId(b)}`;

  const valueFor = (doc: PrequalDocument, b: PrequalDocumentBlock) => {
    const k = editKey(doc, b);
    return edits[k] ?? baselineOf(b);
  };
  const isDirty = (doc: PrequalDocument, b: PrequalDocumentBlock) => {
    const k = editKey(doc, b);
    return edits[k] !== undefined && edits[k] !== baselineOf(b);
  };

  const setEdit = (k: string, v: string) => {
    setEdits((prev) => ({ ...prev, [k]: v }));
    setBlockErrors((prev) => (prev[k] ? { ...prev, [k]: [] } : prev));
  };
  const clearEdit = (k: string) =>
    setEdits((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });

  // Drop just the dismissible Warning items for a block, keeping any Errors.
  const dismissWarnings = (k: string) =>
    setBlockErrors((prev) => {
      const kept = (prev[k] ?? []).filter((e) => e.severity !== "Warning");
      return { ...prev, [k]: kept };
    });

  // Group server block-tagged items onto their editKey for the given document.
  const groupByEditKey = (doc: PrequalDocument, items: PrequalDocumentBlockError[]) => {
    const map: Record<string, PrequalDocumentBlockError[]> = {};
    for (const it of items) {
      const k = `${doc.documentKey}|${it.blockKey}|${it.variant}`;
      (map[k] ??= []).push(it);
    }
    return map;
  };

  const handleSave = (doc: PrequalDocument, b: PrequalDocumentBlock) => {
    const k = editKey(doc, b);
    void save
      .mutateAsync({ documentKey: doc.documentKey, blockKey: b.blockKey, variant: b.variant, text: valueFor(doc, b), partnerBrandId })
      .then((result) => {
        clearEdit(k);
        // The draft is stored; surface any Warning / Error findings inline on
        // the block (a warning never blocks the save — publish is the gate).
        const items = (result?.errors ?? []).map((e) => ({ ...e, severity: e.severity ?? "Error" }));
        setBlockErrors((prev) => ({ ...prev, [k]: items }));
        toast.success("Draft saved.");
      })
      .catch((e) => {
        const tagged = readBlockErrors(e);
        const items: PrequalDocumentBlockError[] =
          tagged.length > 0
            ? tagged
            : [{ blockKey: b.blockKey, variant: b.variant, message: extractErrorMessage(e), severity: "Error" }];
        setBlockErrors((prev) => ({ ...prev, [k]: items }));
      });
  };

  const handleReset = (doc: PrequalDocument, b: PrequalDocumentBlock) => {
    const k = editKey(doc, b);
    void reset
      .mutateAsync({ documentKey: doc.documentKey, blockKey: b.blockKey, variant: b.variant, partnerBrandId })
      .then(() => {
        clearEdit(k);
        toast.success("Reset to default.");
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const openPublish = () => {
    setPublishStep("confirm");
    setPublishViolations([]);
    setOverrideAck(false);
    setPublishOpen(true);
  };

  const closePublish = () => {
    setPublishOpen(false);
    setPublishStep("confirm");
    setPublishViolations([]);
    setOverrideAck(false);
  };

  const handlePublish = (doc: PrequalDocument, overrideWarnings = false) => {
    void publish
      .mutateAsync({ documentKey: doc.documentKey, overrideWarnings, partnerBrandId })
      .then((updated) => {
        setEdits({});
        setBlockErrors({});
        closePublish();
        toast.success(`Published — ${updated.label} is now live.`);
      })
      .catch((e) => {
        const tagged = readBlockErrors(e);
        if (tagged.length === 0) {
          toast.error(extractErrorMessage(e));
          return;
        }
        // Surface each violation inline on its block …
        setBlockErrors(groupByEditKey(doc, tagged));
        // … and steer the dialog: any Error blocks publish; Warnings-only offers override.
        const hasError = tagged.some((t) => t.severity === "Error");
        setPublishViolations(tagged);
        setOverrideAck(false);
        setPublishStep(hasError ? "errors" : "override");
      });
  };

  const handleRestore = (doc: PrequalDocument, version: PrequalDocumentVersion) => {
    void restore
      .mutateAsync({ documentKey: doc.documentKey, versionId: version.id, partnerBrandId })
      .then(() => {
        setEdits({});
        setRestoreVersion(null);
        toast.success("Document restored.");
      })
      .catch((e) => toast.error(extractErrorMessage(e)));
  };

  const handlePreview = (doc: PrequalDocument) => {
    // Send every editable block's current working text; locked blocks resolve
    // to their default server-side.
    const blocks = doc.blocks
      .filter((b) => !b.locked)
      .map((b) => ({ blockKey: b.blockKey, variant: b.variant, text: valueFor(doc, b) }));
    setPreview({ url: "", loading: true });
    void prequalDocumentsApi
      .preview(doc.documentKey, blocks, partnerBrandId)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        setPreview({ url, loading: false });
      })
      .catch((e) => {
        setPreview(null);
        toast.error(extractErrorMessage(e));
      });
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // Switch the active document set. Reset any per-set edit / validation state and
  // close open modals so a dirty state from one set never bleeds into another;
  // keep the active document tab (the tabs are identical for every set — one per
  // PrequalDocumentKeys.All entry).
  //
  // This comment used to state the count. It said "5" for two document keys longer
  // than it was true, was corrected to "8", and was stale again three keys later.
  // A count of something the server enumerates is decoration in a client comment;
  // the sentence says where to look instead.
  const onSelectSet = (next: string | null) => {
    setPartnerBrandId(next);
    setEdits({});
    setBlockErrors({});
    closePublish();
    closePreview();
    setRestoreVersion(null);
  };

  if (query.isLoading) {
    return (
      <>
        <PageHeader eyebrow="Deal Flow · Prequalification" title="Document language" />
        <div className="text-gray-mid">Loading documents…</div>
      </>
    );
  }
  if (query.isError || !active) {
    return (
      <>
        <PageHeader eyebrow="Deal Flow · Prequalification" title="Document language" />
        <div className="text-status-error">Could not load the document language editor.</div>
      </>
    );
  }

  const draftedBlocks = active.blocks.filter((b) => b.hasDraft);
  const unsavedCount = active.blocks.filter((b) => isDirty(active, b)).length;

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Deal Flow · Prequalification"
        title="Document language"
        description="Edit the prose of the buyer / seller letters, deal sheets, the finalize email, and the public funnel's consent and fine print. Edits are saved as drafts, previewed with sample data, and go live only when you publish. Edit the wording; the fixed layout and computed figures are generated in code."
      />

      <div className="flex flex-col gap-space-1 max-w-sm">
        <label htmlFor="prequal-document-set" className="text-caption text-gray-mid">
          Document set
        </label>
        <Select
          id="prequal-document-set"
          value={partnerBrandId ?? ""}
          onChange={(e) => onSelectSet(e.target.value || null)}
          disabled={setsQuery.isLoading}
        >
          {setsQuery.isLoading ? (
            <option value="">Phoenix Default</option>
          ) : (
            (setsQuery.data ?? []).map((s: PrequalDocumentSet) => (
              <option key={s.id ?? "phoenix-default"} value={s.id ?? ""}>
                {s.label}
              </option>
            ))
          )}
        </Select>
      </div>

      <Tabs
        tabs={docs.map((d) => ({ id: d.documentKey, label: d.label }))}
        activeId={active.documentKey}
        onChange={(id) => setActiveKey(id)}
        ariaLabel="Prequalification documents"
      >
        <div className="flex flex-wrap items-center justify-between gap-space-3 py-space-3">
          <div className="flex items-center gap-space-2">
            <span className="text-caption text-gray-mid uppercase tracking-wide">
              {active.audienceLabel}
            </span>
            {draftedBlocks.length > 0 && (
              <StatusBadge tone="warn">{draftedBlocks.length} unpublished</StatusBadge>
            )}
          </div>
          <div className="flex items-center gap-space-2">
            <Button variant="secondary" onClick={() => handlePreview(active)}>
              Preview
            </Button>
            <Button
              onClick={openPublish}
              disabled={draftedBlocks.length === 0}
              title={draftedBlocks.length === 0 ? "No drafted changes to publish" : undefined}
            >
              Publish…
            </Button>
          </div>
        </div>

        {unsavedCount > 0 && (
          <p className="mb-space-3 text-caption text-status-warn">
            You have {unsavedCount} unsaved edit{unsavedCount === 1 ? "" : "s"}. Save each block before publishing.
          </p>
        )}

        <div className="flex flex-col gap-space-3">
          {active.blocks.map((b) => {
            const k = editKey(active, b);
            return (
              <BlockEditor
                key={blockId(b)}
                block={b}
                value={valueFor(active, b)}
                dirty={isDirty(active, b)}
                errors={blockErrors[k] ?? []}
                saving={save.isPending}
                resetting={reset.isPending}
                onChange={(v) => setEdit(k, v)}
                onSave={() => handleSave(active, b)}
                onReset={() => handleReset(active, b)}
                onDismissWarnings={() => dismissWarnings(k)}
              />
            );
          })}
        </div>

        <HistoryPanel
          document={active}
          formatDate={formatDate}
          onRestore={(v) => setRestoreVersion(v)}
        />
      </Tabs>

      {/* Preview */}
      <Modal
        open={preview !== null}
        onClose={closePreview}
        size="xl"
        title={`Preview — ${active.label}`}
        description="Rendered with sample data. Nothing here is saved."
      >
        {preview?.loading || !preview?.url ? (
          <div className="text-gray-mid py-space-5 text-center">Rendering preview…</div>
        ) : (
          <iframe
            title="Document preview"
            src={preview.url}
            className="w-full h-[70vh] rounded-md border border-border-light bg-white-pure"
          />
        )}
      </Modal>

      {/* Publish confirm / override / errors */}
      <Modal
        open={publishOpen}
        onClose={closePublish}
        size="lg"
        title={
          publishStep === "override"
            ? "Review warnings before publishing"
            : publishStep === "errors"
              ? "Can’t publish yet"
              : `Publish ${active.label}?`
        }
        description={
          publishStep === "override"
            ? "These blocks publish with advisories. Acknowledge them to continue."
            : publishStep === "errors"
              ? "Fix the blocking problems below, then publish again."
              : "These blocks will go live on the very next generated document."
        }
        footer={
          <div className="flex w-full items-center justify-end gap-space-3">
            <Button variant="secondary" onClick={closePublish} disabled={publish.isPending}>
              {publishStep === "errors" ? "Close" : "Cancel"}
            </Button>
            {publishStep === "confirm" && (
              <Button onClick={() => handlePublish(active, false)} loading={publish.isPending}>
                Publish
              </Button>
            )}
            {publishStep === "override" && (
              <Button
                onClick={() => handlePublish(active, true)}
                loading={publish.isPending}
                disabled={!overrideAck}
              >
                Publish anyway
              </Button>
            )}
          </div>
        }
      >
        {publishStep === "confirm" && (
          <>
            <ul className="flex flex-col gap-space-2">
              {draftedBlocks.map((b) => (
                <li key={blockId(b)} className="text-small">
                  <span className="font-semibold">{b.label}</span>
                </li>
              ))}
            </ul>
            {unsavedCount > 0 && (
              <p className="mt-space-3 text-caption text-status-warn">
                {unsavedCount} unsaved edit{unsavedCount === 1 ? "" : "s"} will not be published — save them first.
              </p>
            )}
          </>
        )}

        {publishStep === "override" && (
          <div className="flex flex-col gap-space-3">
            <ul className="flex flex-col gap-space-2">
              {publishViolations.map((v, i) => (
                <li
                  key={`${blockId(v)}-${i}`}
                  className="rounded-md border border-status-warning bg-status-warning/10 p-space-2"
                >
                  <div className="text-small font-semibold text-onyx">{labelForViolation(active, v)}</div>
                  <div className="text-caption text-onyx">{v.message}</div>
                </li>
              ))}
            </ul>
            <label className="flex items-start gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                checked={overrideAck}
                onChange={(e) => setOverrideAck(e.target.checked)}
                className="mt-[3px]"
              />
              <span>I understand — publish anyway.</span>
            </label>
          </div>
        )}

        {publishStep === "errors" && (
          <ul className="flex flex-col gap-space-2">
            {publishViolations.map((v, i) => (
              <li
                key={`${blockId(v)}-${i}`}
                className="rounded-md border border-status-error bg-status-error/10 p-space-2"
              >
                <div className="text-small font-semibold text-onyx">{labelForViolation(active, v)}</div>
                <div className="text-caption text-status-error">{v.message}</div>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      {/* Restore confirm */}
      <Modal
        open={restoreVersion !== null}
        onClose={() => setRestoreVersion(null)}
        size="md"
        title="Restore this version?"
        description="Replaces the document's published copy with this snapshot. Blocks not in the snapshot reset to their code default."
        footer={
          <div className="flex w-full items-center justify-end gap-space-3">
            <Button variant="secondary" onClick={() => setRestoreVersion(null)} disabled={restore.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => restoreVersion && handleRestore(active, restoreVersion)}
              loading={restore.isPending}
            >
              Restore
            </Button>
          </div>
        }
      >
        <p className="text-small text-gray-mid">This publishes a new version — it does not delete history.</p>
      </Modal>
    </div>
  );
}

// ── one block editor ─────────────────────────────────────────────────────────

function BlockEditor({
  block,
  value,
  dirty,
  errors,
  saving,
  resetting,
  onChange,
  onSave,
  onReset,
  onDismissWarnings,
}: {
  block: PrequalDocumentBlock;
  value: string;
  dirty: boolean;
  errors: PrequalDocumentBlockError[];
  saving: boolean;
  resetting: boolean;
  onChange: (v: string) => void;
  onSave: () => void;
  onReset: () => void;
  onDismissWarnings: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  const errorItems = errors.filter((e) => e.severity === "Error");
  const warningItems = errors.filter((e) => e.severity === "Warning");
  // Informational only (R3/R4): never an alert, never `invalid`, never gates Save.
  const note = blockNoteFor(block, value);

  const insertToken = (token: string) => {
    const snippet = `{{${token}}}`;
    const ta = ref.current;
    if (!ta) {
      onChange(value + snippet);
      return;
    }
    const start = ta.selectionStart ?? value.length;
    const end = ta.selectionEnd ?? value.length;
    onChange(value.slice(0, start) + snippet + value.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + snippet.length;
      ta.setSelectionRange(pos, pos);
    });
  };

  return (
    <Card data-testid={`block-${block.blockKey}-${block.variant}`}>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-space-2">
          <CardTitle className="text-small">{block.label}</CardTitle>
          <div className="flex items-center gap-space-2">
            {block.locked ? (
              <StatusBadge tone="muted">Fixed layout</StatusBadge>
            ) : block.isCustomized ? (
              <StatusBadge tone="info">Customized</StatusBadge>
            ) : (
              <StatusBadge tone="muted">Default</StatusBadge>
            )}
            {block.hasDraft && <StatusBadge tone="warn">Draft</StatusBadge>}
            {dirty && <StatusBadge tone="warn">Unsaved</StatusBadge>}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {block.locked ? (
          <div className="flex flex-col gap-space-2">
            <Textarea rows={3} value={block.effectiveText} readOnly disabled aria-label={block.label} />
            <p className="text-caption text-gray-mid">
              {"Part of the document's fixed layout — generated or set in code."}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-space-2">
            <Textarea
              ref={ref}
              rows={4}
              value={value}
              aria-label={block.label}
              invalid={errorItems.length > 0}
              onChange={(e) => onChange(e.target.value)}
            />
            {block.allowedTokens.length > 0 && (
              <div className="flex flex-wrap items-center gap-space-2">
                <span className="text-caption text-gray-mid">Insert:</span>
                {block.allowedTokens.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => insertToken(t)}
                    className="rounded-full border border-border-light bg-white-warm px-space-2 py-[2px] font-mono text-caption text-onyx hover:border-gold"
                  >
                    {`{{${t}}}`}
                    {block.requiredTokens.includes(t) ? " *" : ""}
                  </button>
                ))}
              </div>
            )}
            {note && <p className="text-caption text-gray-mid">{note}</p>}
            {errorItems.length > 0 && (
              <ul role="alert" className="flex flex-col gap-[2px]">
                {errorItems.map((e, i) => (
                  <li key={i} className="text-caption text-status-error">
                    {e.message}
                  </li>
                ))}
              </ul>
            )}
            {warningItems.length > 0 && (
              <div
                role="status"
                className="flex items-start justify-between gap-space-2 rounded-md border border-status-warning bg-status-warning/10 p-space-2"
              >
                <ul className="flex flex-col gap-[2px]">
                  {warningItems.map((w, i) => (
                    <li key={i} className="text-caption text-onyx">
                      {w.message}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={onDismissWarnings}
                  aria-label="Dismiss warning"
                  className="shrink-0 text-caption text-gray-mid hover:text-onyx"
                >
                  Dismiss
                </button>
              </div>
            )}
            <div className="flex items-center gap-space-2">
              <Button size="sm" onClick={onSave} loading={saving} disabled={!dirty}>
                Save
              </Button>
              {(block.isCustomized || block.hasDraft) && (
                <Button size="sm" variant="text" onClick={onReset} loading={resetting}>
                  Reset to default
                </Button>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── version history ──────────────────────────────────────────────────────────

function HistoryPanel({
  document,
  formatDate,
  onRestore,
}: {
  document: PrequalDocument;
  formatDate: (iso: string) => string;
  onRestore: (v: PrequalDocumentVersion) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <Card className="mt-space-4">
      <CardHeader>
        <CardTitle className="text-small">Version history</CardTitle>
      </CardHeader>
      <CardContent>
        {document.versions.length === 0 ? (
          <span className="text-caption text-gray-mid">No published versions yet.</span>
        ) : (
          <ul className="flex flex-col gap-space-3">
            {document.versions.map((v) => (
              <li key={v.id} className="border border-border-light rounded-md p-space-3">
                <div className="flex flex-wrap items-center justify-between gap-space-2">
                  <div className="flex items-center gap-space-2 text-small">
                    <span className="font-semibold">{formatDate(v.publishedAtUtc)}</span>
                    <span className="text-gray-mid">
                      {v.publishedByUserId ?? "system"} · {v.changedBlockCount} block
                      {v.changedBlockCount === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="flex items-center gap-space-2">
                    <Button
                      size="sm"
                      variant="text"
                      onClick={() => setOpenId((cur) => (cur === v.id ? null : v.id))}
                    >
                      {openId === v.id ? "Hide changes" : "View changes"}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => onRestore(v)}>
                      Restore…
                    </Button>
                  </div>
                </div>
                {openId === v.id && (
                  <div className="mt-space-3 flex flex-col gap-space-3">
                    {v.blocks.map((b) => (
                      <div key={`${b.blockKey}|${b.variant}`} className="text-caption">
                        <div className="font-semibold text-onyx">{b.label}</div>
                        <div className="mt-[2px] grid grid-cols-1 gap-space-2 md:grid-cols-2">
                          <div>
                            <Badge status="draft">Default</Badge>
                            <p className="mt-[2px] whitespace-pre-wrap text-gray-mid">{b.defaultText}</p>
                          </div>
                          <div>
                            <Badge status="info">Published</Badge>
                            <p className="mt-[2px] whitespace-pre-wrap text-onyx">{b.publishedText}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
